<template>
  <BCard>
    <BListItem class="mb-400">
      <template #header>
        <span>{{ $t(`client.name`) }}</span>
      </template>
      <BInput
        v-model="internalClient.name"
        v-model:valid="nameValid"
        :rules="[requiredRule]"
        :max-length="255"
        :placeholder="$t('client.placeholder.name')"
      />
    </BListItem>
    <BListItem class="mb-600">
      <template #header>
        <span>{{ $t(`client.slug`) }}</span>
      </template>
      <BInput
        v-model="internalClient.slug"
        v-model:valid="slugValid"
        :rules="[requiredRule, slugRule]"
        :max-length="32"
        :min-length="4"
        :placeholder="$t('client.placeholder.slug')"
        @keypress-enter="save"
      />
    </BListItem>
    <BListItem class="mb-600">
      <template #header>
        <span>{{ $t(`client.expiredAt`) }}</span>
      </template>
      <BInputDateTime
        v-model:date="internalClient.expiredAt"
        v-model:time="internalClient.expiredAt"
        date-format="YYYY/MM/DD"
      />
    </BListItem>
    <BListItem
      v-if="!isProduction"
      class="mb-600"
    >
      <template #header>
        <span>{{ $t(`client.expiredClientDataPurge.title`) }}</span>
      </template>
      <BTooltip
        :content="$t(`client.expiredClientDataPurge.explain`)"
        top
      >
        <BSwitch
          :status="internalClient.expiredClientDataPurge"
          :before-change="handleExpiredClientDataPurgeChange"
          :loading="loading"
          color="accent"
        />
      </BTooltip>
    </BListItem>
    <BListItem class="mb-600">
      <template #header>
        <span>{{ $t(`client.status.title`) }}</span>
      </template>
      <BSelect
        v-model="internalClient.status"
        :items="clientStatuses"
        item-text="text"
        item-value="value"
        fit
      />
    </BListItem>
    <BListItem class="mb-600">
      <template #header>
        <span>{{ $t(`client.tenant_type.title`) }}</span>
      </template>
      <BSelect
        v-model="internalClient.tenantType"
        :items="clientTenantTypes"
        item-text="text"
        item-value="value"
        fit
      />
    </BListItem>
    <BListItem
      v-if="isAppTypeRequired"
      class="mb-600"
    >
      <template #header>
        <span>{{ $t(`client.appType.title`) }}</span>
      </template>
      <BSelect
        v-model="internalClient.appType"
        :disabled="!enableAppTypeSelect"
        :items="clientAppTypes"
        item-text="text"
        item-value="value"
        fit
      />
    </BListItem>
    <BLayout
      align-center
      justify-center
    >
      <BBtn
        class="mr-400"
        text
        @click="cancel"
      >
        {{ $t(`general.cancel.text`) }}
      </BBtn>
      <BBtn
        class="ml-400"
        type="primary"
        :loading="loading"
        :disabled="!isValid"
        @click="save"
      >
        {{ $t(`general.save.text`) }}
      </BBtn>
    </BLayout>
  </BCard>
</template>

<script>
import inputValidation from '@/mixins/input_validation';

const DEFAULT_APP_TYPE = 'crm';

export default {
  mixins: [inputValidation],
  props: {
    client: {
      type: Object,
      default () {
        return {
          name: '',
          slug: '',
          expiredAt: '',
          expiredClientDataPurge: false,
          status: '',
          tenantType: '',
          appType: DEFAULT_APP_TYPE,
        };
      },
    },
    loading: Boolean,
  },
  emits: [
    'save',
    'cancel',
  ],
  data () {
    return {
      nameValid: false,
      slugValid: false,
      internalClient: this.client,
    };
  },
  computed: {
    isValid () {
      return this.nameValid && this.slugValid && this.expiredAtValid && this.statusValid && this.tenantTypeValid;
    },
    expiredAtValid () {
      return !!this.internalClient.expiredAt;
    },
    statusValid () {
      return !!this.internalClient.status;
    },
    tenantTypeValid () {
      return !!this.internalClient.tenantType;
    },
    clientStatuses () {
      return Object.entries(this.$tm('client.status.enum')).map(([k, v]) => {
        return { value: k, text: this.$rt(v) };
      });
    },
    clientTenantTypes () {
      return Object.entries(this.$tm('client.tenant_type.enum')).map(([k, v]) => {
        return { value: k, text: this.$rt(v) };
      });
    },
    clientAppTypes () {
      return Object.entries(this.$tm('client.appType.enum')).map(([k, v]) => {
        const value = (() => {
          if (k === 'voiceAnalytics') return 'voice_analytics';
          if (k === 'crm') return 'crm';
        })();
        return { value, text: this.$rt(v) };
      });
    },
    isAppTypeRequired () {
      if (process.env.NODE_ENV === 'production') return false;
      return this.internalClient.tenantType === 'bales_bpo';
    },
    isNewClient() {
      return !this.internalClient.id;
    },
    enableAppTypeSelect() {
      return this.isNewClient;
    },
    isProduction() {
      return process.env.NODE_ENV === 'production';
    },
  },
  watch: {
    isAppTypeRequired: {
      handler(newVal) {
        if (!newVal) {
          this.internalClient.appType = DEFAULT_APP_TYPE;
        }
      },
    },
  },
  methods: {
    save () {
      if (!this.isValid) return;
      const client = {
        name: this.internalClient.name,
        slug: this.internalClient.slug,
        expiredAt: this.internalClient.expiredAt,
        expiredClientDataPurge: this.internalClient.expiredClientDataPurge,
        status: this.internalClient.status,
        tenantType: this.internalClient.tenantType,
        appType: this.internalClient.appType,
      };
      this.$emit('save', client);
    },
    reset () {
      this.internalClient.name = '';
      this.internalClient.slug = '';
      this.internalClient.expiredAt = '';
      this.internalClient.expiredClientDataPurge = false;
      this.internalClient.status = '';
      this.internalClient.tenantType = '';
      this.internalClient.appType = '';
    },
    cancel () {
      this.$emit('cancel');
    },
    async handleExpiredClientDataPurgeChange() {
      const newValue = !this.internalClient.expiredClientDataPurge;
      if (newValue) {
        const isWarningAccepted = await this.$bitterAlert.show({
          title: this.$t('client.expiredClientDataPurge.title'),
          text: this.$t('client.expiredClientDataPurge.confirmText'),
        });
        if (!isWarningAccepted) { return; }
      }
      this.internalClient.expiredClientDataPurge = newValue;
    },
  },
};
</script>
